import { useConfigContext } from '@fe-core';
import { HeaderConfigItemSchema } from '@fe-core-events';
import { TTeam } from '@fe-learner-common/api/schemas/teams';
import { buildPath } from '@fe-learner-common/hooks/useHeaderConfig';
import { useTeams } from '@fe-learner-common/hooks/data/useTeams';
import { useBetaCookie } from '@fe-learner-common/hooks/useBetaCookie';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

type HeaderMenuItem = {
  order: number;
} & HeaderConfigItemSchema;

export const useTeamviewHeaderItem = ({
  active = false,
}: {
  active?: boolean;
} = {}): {
  enabled: boolean;
  item: HeaderMenuItem;
} | null => {
  const intl = useIntl();
  const { config, extension } = useConfigContext();
  const isLoggedIn = config?.loggedIn;
  const { data: teamsData } = useTeams(
    isLoggedIn && config?.webfeatures?.['teamview'],
  );
  const { enabled: betaEnabled } = useBetaCookie({
    cookieName: 'fe-learner-teamview',
  });
  const enabledByDefault = config?.webfeatures?.['fe-learner-teamview-default'];

  const shouldSeeTeamView =
    isLoggedIn &&
    config?.webfeatures?.['teamview'] &&
    teamsData &&
    !!teamsData?.length;

  return useMemo(() => {
    if (!shouldSeeTeamView) {
      return null;
    }

    const { basename } = extension?.microfrontend || {};

    if (!betaEnabled && !enabledByDefault) {
      return {
        enabled: true,
        item: {
          order: 4,
          name: intl.formatMessage({ id: 'courses.header.menu.team' }),
          value: teamsData.map((team: TTeam) => ({
            name: team.name,
            type: 'link' as const,
            value: buildPath(basename, `/team/overview/${team.teamId}`),
          })),
        },
      };
    }

    return {
      enabled: true,
      item: {
        order: 4,
        active,
        name:
          teamsData.length > 1
            ? intl.formatMessage({ id: 'courses.header.menu.teams' })
            : intl.formatMessage({ id: 'courses.header.menu.team' }),
        type: 'link' as const,
        value:
          teamsData.length > 1
            ? buildPath(basename, '/teams')
            : buildPath(basename, `/team/${teamsData[0]?.teamId}`),
      },
    };
  }, [
    shouldSeeTeamView,
    betaEnabled,
    enabledByDefault,
    active,
    teamsData,
    intl,
  ]);
};
